import { Skeleton } from '@mui/material';
import moment from 'moment';
import { type JSX, useEffect, useState } from 'react';

import { GroupedBarsChart } from 'Components/BaseCharts/GroupedBarsChart';
import { CSSColors } from 'Components/Colors';
import { FormFieldDateOptionSelect } from 'Components/FormField/FormFieldDateOptionSelect/FormFieldDateOptionSelect';
import { CustomDateRangeModal } from 'Components/Modal/CustomDateRangeModal/CustomDateRangeModal';
import { Text } from 'Components/Text/Text';
import { toTitleCase } from 'Helpers/StringUtils';
import { useDateFilter } from 'Hooks/DateFilter';
import { Action, ActionStatus } from 'Models/Actions';
import { DateOptionSelectOption, DefaultPastDateRangeOptions } from 'Models/Dashboards';

import { ActionDisplayModal, ActionModalDisplayType } from './ActionDisplayModal';
import styles from './RiskRegisterDashboard.module.css';
import { countByMonth, countClosedActionsByMonth, countTotalActionsByMonth, getMonths } from '../Helpers/Dashboards.helpers';

enum Modals {
    NONE,
    DISPLAY_ACTIONS,
    CUSTOM_DATE,
}

export interface ActionHistoryProps {
    allActions?: Action[];
    actionsError?: string;
}

export const ActionHistory = (props: ActionHistoryProps): JSX.Element => {
    const [modalDisplayType, setModalDisplayType] = useState<ActionModalDisplayType>();
    const [modalState, setModalState] = useState<Modals>(Modals.NONE);

    const [clickedActions, setClickedActions] = useState<Action[]>();
    const [clickedDate, setClickedDate] = useState<string>();
    const [closedActions, setClosedActions] = useState<number[]>();
    const [monthLabel, setMonthLabel] = useState<string[]>();
    const [openedActions, setOpenedActions] = useState<number[]>();
    const [totalOpenActions, setTotalOpenActions] = useState<number[]>();

    const dateFilterHookValues = useDateFilter(DateOptionSelectOption.LAST_THREE_MONTHS);

    /** Update the chart data when Actions are loaded or when the date filter is changed. */
    useEffect(() => {
        if (props.allActions) {
            const allClosedActions = props.allActions.filter((action) => action.status === ActionStatus.CLOSED);

            const months = getMonths(dateFilterHookValues.selection.dateRange.startDate, dateFilterHookValues.selection.dateRange.endDate);

            const openedActions = countByMonth(props.allActions, months);
            const actionsClosedByMonth = countClosedActionsByMonth(allClosedActions, months);
            const totalActions = countTotalActionsByMonth(props.allActions, months);

            setClosedActions(actionsClosedByMonth);
            setOpenedActions(openedActions);
            setMonthLabel(months);
            setTotalOpenActions(totalActions);
        }
    }, [props.allActions, dateFilterHookValues]);

    /** Get all closed Actions for a specific month. */
    const getClosedActionsPerMonth = (actions: Action[], month: string) => {
        return actions.filter((action) => action.status === ActionStatus.CLOSED && moment(action.closed_timestamp).format('MMM YY') === month);
    };

    /** Pop up the modal to shows Action details if a bar on the chart is clicked. */
    const handleChartClick = (item: string) => {
        const clickedItem = item.split(' | ');
        const clickedDate = toTitleCase(clickedItem[0]);
        if (clickedItem[1] === 'Total Closed' && props.allActions) {
            const closedActions = props.allActions.filter((action) => action.status === ActionStatus.CLOSED);
            setClickedActions(getClosedActionsPerMonth(closedActions, clickedDate));
            setClickedDate(clickedDate);
            setModalState(Modals.DISPLAY_ACTIONS);
            setModalDisplayType(ActionModalDisplayType.CLOSED);
        } else if (clickedItem[1] === 'Total Opened' && props.allActions) {
            const openActions = props.allActions.filter((action) => moment(action.created_timestamp).format('MMM YY') === clickedDate);
            setClickedActions(openActions);
            setClickedDate(clickedDate);
            setModalState(Modals.DISPLAY_ACTIONS);
            setModalDisplayType(ActionModalDisplayType.OPEN);
        }
    };

    if (props.actionsError) {
        return (
            <>
                <div className={styles.dashletHeaderWithTitleWithFilter}>
                    <Text variant="Header2" color="white">
                        Action History
                    </Text>
                </div>
                <Text color="white" variant="Header4">
                    {props.actionsError}
                </Text>
            </>
        );
    } else {
        return (
            <>
                {modalState === Modals.CUSTOM_DATE && <CustomDateRangeModal datesRestriction="pastDatesOnly" hideModal={() => setModalState(Modals.NONE)} dateFilterHookValues={dateFilterHookValues} />}
                {modalState === Modals.DISPLAY_ACTIONS && clickedDate && clickedActions && modalDisplayType !== undefined && <ActionDisplayModal selectedActions={clickedActions} date={clickedDate} hideModal={() => setModalState(Modals.NONE)} modalType={modalDisplayType} />}
                <div className={styles.dashletHeaderWithTitleWithFilter}>
                    <Text variant="Header2" color="white">
                        Action History
                    </Text>
                    <FormFieldDateOptionSelect formFieldId="date" options={DefaultPastDateRangeOptions} onSelectCustom={() => setModalState(Modals.CUSTOM_DATE)} dateFilterHookValues={dateFilterHookValues} />
                </div>
                {closedActions && monthLabel && totalOpenActions && openedActions ? (
                    <GroupedBarsChart
                        onChartClick={handleChartClick}
                        xAxisLabels={monthLabel}
                        line={{ name: 'Total Actions', color: CSSColors.YELLOW, data: totalOpenActions }}
                        barsSeriesArray={[
                            { name: 'Total Opened', color: CSSColors.LIGHT_BLUE, data: openedActions },
                            { name: 'Total Closed', color: CSSColors.GRAY, data: closedActions },
                        ]}
                        yAxisInfo={[
                            { name: 'Total Closed/Opened', position: 'left', minInterval: 1 },
                            { name: 'Total Actions', position: 'right', minInterval: 1 },
                        ]}
                        groupingMode="clustered"
                    />
                ) : (
                    <Skeleton variant="rounded" width={'100%'} height={300} />
                )}
            </>
        );
    }
};
